import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  CrownOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import SubscriptionBillingSettings from "./components/SubscriptionBillingSettings";
import SubscriptionOnlineSettings from "./components/SubscriptionOnlineSettings";
import {
  formatSubscriptionNumber,
} from "./subscriptionFormatters";
import "./SubscriptionPlans.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;
const SEARCH_DELAY_MS = 320;

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  billing_cycle: "monthly" | "quarterly" | "annual";
  features: string[];
  max_users: number | null;
  max_companies: number | null;
  storage_limit_gb: number | null;
  is_trial: boolean;
  trial_duration_days: number;
  is_active: boolean;
  is_public: boolean;
  sort_order: number;
  companies_count: number;
  created_at: string;
  updated_at: string;
}

interface PlanStats {
  total: number;
  active: number;
  inactive: number;
  trial: number;
  assigned_companies: number;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface PlanFormValues {
  name: string;
  description?: string;
  features?: string;
  max_users?: number | null;
  max_companies: number;
  storage_limit_gb?: number | null;
  is_trial: boolean;
  trial_duration_days: number;
  is_active: boolean;
  is_public: boolean;
  sort_order: number;
}

const EMPTY_STATS: PlanStats = {
  total: 0,
  active: 0,
  inactive: 0,
  trial: 0,
  assigned_companies: 0,
};

const SubscriptionPlans: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [stats, setStats] = useState<PlanStats>(EMPTY_STATS);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mutatingPlanId, setMutatingPlanId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form] = Form.useForm<PlanFormValues>();
  const requestRef = useRef<AbortController | null>(null);
  const { hasPermission, user } = useAuth();
  const canView = hasPermission("Admin.Souscription.Plans.view");
  const canManage = hasPermission("Admin.Souscription.Plans.manage");

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchText.trim()),
      searchText ? SEARCH_DELAY_MS : 0,
    );
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const fetchPlans = useCallback(
    async (page = 1, limit = 10) => {
      if (!canView || !user?.is_superadmin) return;

      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          status: statusFilter,
        });
        if (debouncedSearch) params.set("search", debouncedSearch);

        const response = await fetch(`/api/subscription/plans?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible de charger les plans");
        }

        setPlans(payload.data.plans || []);
        setStats({ ...EMPTY_STATS, ...(payload.data.stats || {}) });
        setPagination(payload.data.pagination);
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          message.error(
            error instanceof Error
              ? error.message
              : "Impossible de charger les plans",
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [canView, debouncedSearch, statusFilter, user?.is_superadmin],
  );

  useEffect(() => {
    fetchPlans(1, pagination.per_page);
    return () => requestRef.current?.abort();
    // La taille courante est conservée lorsqu'un filtre change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlans]);

  const openPlanModal = useCallback(
    (plan?: SubscriptionPlan) => {
      const selectedPlan = plan || null;
      setEditingPlan(selectedPlan);
      form.resetFields();
      form.setFieldsValue(
        selectedPlan
          ? {
              ...selectedPlan,
              description: selectedPlan.description || "",
              features: selectedPlan.features.join("\n"),
              max_companies: selectedPlan.max_companies || 1,
            }
          : {
              max_companies: 1,
              trial_duration_days: 30,
              is_trial: false,
              is_active: true,
              is_public: false,
              sort_order: 0,
            },
      );
      setModalOpen(true);
    },
    [form],
  );

  const closePlanModal = useCallback(() => {
    setModalOpen(false);
    setEditingPlan(null);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(
    async (values: PlanFormValues) => {
      setSaving(true);
      try {
        const planData = {
          ...values,
          description: values.description?.trim() || "",
          features: (values.features || "")
            .split("\n")
            .map((feature) => feature.trim())
            .filter(Boolean),
          max_users: values.max_users ?? null,
          storage_limit_gb: values.storage_limit_gb ?? null,
        };
        const response = await fetch(
          editingPlan
            ? `/api/subscription/plans/${editingPlan.id}`
            : "/api/subscription/plans",
          {
            method: editingPlan ? "PUT" : "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(planData),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible d'enregistrer le plan");
        }

        message.success(payload.message);
        closePlanModal();
        await fetchPlans(pagination.current_page, pagination.per_page);
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Impossible d'enregistrer le plan",
        );
      } finally {
        setSaving(false);
      }
    },
    [closePlanModal, editingPlan, fetchPlans, pagination],
  );

  const updatePlanStatus = useCallback(
    async (plan: SubscriptionPlan, isActive: boolean) => {
      setMutatingPlanId(plan.id);
      try {
        const response = await fetch(`/api/subscription/plans/${plan.id}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_active: isActive }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible de modifier le statut");
        }

        message.success(isActive ? "Plan activé" : "Plan désactivé");
        await fetchPlans(pagination.current_page, pagination.per_page);
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Modification impossible",
        );
      } finally {
        setMutatingPlanId(null);
      }
    },
    [fetchPlans, pagination],
  );

  const deletePlan = useCallback(
    async (plan: SubscriptionPlan) => {
      setMutatingPlanId(plan.id);
      try {
        const response = await fetch(`/api/subscription/plans/${plan.id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Impossible de supprimer le plan");
        }

        message.success(payload.message);
        await fetchPlans(1, pagination.per_page);
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Suppression impossible",
        );
      } finally {
        setMutatingPlanId(null);
      }
    },
    [fetchPlans, pagination.per_page],
  );

  const columns = useMemo(
    () => [
      {
        title: "Plan",
        key: "plan",
        width: 300,
        render: (_: unknown, plan: SubscriptionPlan) => (
          <div className="plan-management__identity">
            <Avatar
              shape="square"
              icon={plan.is_trial ? <ExperimentOutlined /> : <CrownOutlined />}
              className={plan.is_trial ? "is-trial" : ""}
            />
            <div>
              <Space size={6} wrap>
                <strong>{plan.name}</strong>
                {plan.is_trial && <Tag color="orange">ESSAI</Tag>}
                {plan.is_public && <Tag color="blue">LANDING PAGE</Tag>}
              </Space>
              <Paragraph ellipsis={{ rows: 2 }}>
                {plan.description || "Aucune description"}
              </Paragraph>
            </div>
          </div>
        ),
      },
      {
        title: "Positionnement",
        key: "pricing",
        width: 170,
        render: (_: unknown, plan: SubscriptionPlan) => (
          <div className="plan-management__price">
            <strong>{plan.is_trial ? "Essai" : "Offre fonctionnelle"}</strong>
            <span>Prix géré par Warehouse</span>
          </div>
        ),
      },
      {
        title: "Capacités",
        key: "limits",
        width: 190,
        render: (_: unknown, plan: SubscriptionPlan) => (
          <div className="plan-management__limits">
            <span><UserOutlined />{plan.max_users ? `${formatSubscriptionNumber(plan.max_users)} utilisateurs` : "Utilisateurs illimités"}</span>
            <span><ShopOutlined />{plan.max_companies ? `${formatSubscriptionNumber(plan.max_companies)} entreprise(s)` : "Entreprises illimitées"}</span>
            <span><DatabaseOutlined />{plan.storage_limit_gb ? `${formatSubscriptionNumber(plan.storage_limit_gb)} Go` : "Stockage illimité"}</span>
          </div>
        ),
      },
      {
        title: "Fonctionnalités",
        dataIndex: "features",
        key: "features",
        width: 160,
        render: (features: string[]) => (
          <Tooltip title={features.length ? features.join(" · ") : "Aucune fonctionnalité décrite"}>
            <Tag icon={<CheckCircleOutlined />} color={features.length ? "green" : "default"}>
              {formatSubscriptionNumber(features.length)} décrite(s)
            </Tag>
          </Tooltip>
        ),
      },
      {
        title: "Entreprises",
        dataIndex: "companies_count",
        key: "companies_count",
        align: "center" as const,
        width: 115,
        render: (count: number) => (
          <div className="plan-management__adoption"><TeamOutlined /><strong>{formatSubscriptionNumber(count)}</strong></div>
        ),
      },
      {
        title: "Statut",
        dataIndex: "is_active",
        key: "status",
        width: 110,
        render: (isActive: boolean, plan: SubscriptionPlan) => (
          <Switch
            checked={isActive}
            loading={mutatingPlanId === plan.id}
            disabled={!canManage}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            onChange={(checked) => updatePlanStatus(plan, checked)}
          />
        ),
      },
      {
        title: "",
        key: "actions",
        fixed: "right" as const,
        width: 96,
        render: (_: unknown, plan: SubscriptionPlan) =>
          canManage ? (
            <Space size={2}>
              <Tooltip title="Modifier le plan">
                <Button type="text" icon={<EditOutlined />} onClick={() => openPlanModal(plan)} />
              </Tooltip>
              <Popconfirm
                title="Supprimer ce plan ?"
                description={plan.companies_count ? `${plan.companies_count} entreprise(s) utilisent encore ce plan.` : "Cette action est définitive."}
                disabled={plan.companies_count > 0}
                okText="Supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
                onConfirm={() => deletePlan(plan)}
              >
                <Tooltip title={plan.companies_count ? "Réattribuez d'abord les entreprises" : "Supprimer le plan"}>
                  <Button type="text" danger disabled={plan.companies_count > 0} loading={mutatingPlanId === plan.id} icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          ) : null,
      },
    ],
    [canManage, deletePlan, mutatingPlanId, openPlanModal, updatePlanStatus],
  );

  if (!user?.is_superadmin || !canView) {
    return (
      <div className="plan-management__denied">
        <SettingOutlined />
        <Title level={4}>Accès réservé au SysAdmin</Title>
        <Text type="secondary">La configuration globale des plans n'est pas accessible depuis un compte abonné.</Text>
      </div>
    );
  }

  const statsCards = [
    ["Plans configurés", stats.total, <AppstoreOutlined />, "primary"],
    ["Plans actifs", stats.active, <CheckCircleOutlined />, "success"],
    ["Plans d'essai", stats.trial, <ExperimentOutlined />, "trial"],
    ["Entreprises abonnées", stats.assigned_companies, <TeamOutlined />, "company"],
  ];

  return (
    <div className="plan-management">
      <section className="plan-management__hero">
        <div>
          <span><SettingOutlined /> Catalogue d'abonnement</span>
          <Title level={2}>Configuration des plans</Title>
          <Text>Définissez les fonctionnalités, les capacités et la disponibilité des offres ELSA GESTION.</Text>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openPlanModal()}>
            Nouveau plan
          </Button>
        )}
      </section>

      <Row gutter={[14, 14]} className="plan-management__stats">
        {statsCards.map(([label, value, icon, tone]) => (
          <Col xs={24} sm={12} xl={6} key={String(label)}>
            <Card bordered={false} className={`is-${tone}`}>
              <span>{icon as React.ReactNode}</span>
              <div><strong>{formatSubscriptionNumber(Number(value))}</strong><small>{String(label)}</small></div>
            </Card>
          </Col>
        ))}
      </Row>

      {canManage && <SubscriptionBillingSettings />}

      {canManage && <SubscriptionOnlineSettings />}

      <Card bordered={false} className="plan-management__workspace">
        <div className="plan-management__toolbar">
          <div>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Rechercher par nom, description ou fonctionnalité…"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              suffix={searchText.trim() !== debouncedSearch ? <span className="plan-management__searching">Recherche…</span> : null}
            />
            <small>La recherche se met à jour automatiquement.</small>
          </div>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all", label: "Tous les statuts" },
              { value: "active", label: "Plans actifs" },
              { value: "inactive", label: "Plans inactifs" },
            ]}
          />
          <Button icon={<ReloadOutlined spin={loading} />} disabled={loading} onClick={() => fetchPlans(pagination.current_page, pagination.per_page)}>
            Actualiser
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={plans}
          loading={loading}
          pagination={false}
          scroll={{ x: 1160 }}
          locale={{ emptyText: debouncedSearch ? "Aucun plan ne correspond à cette recherche." : "Aucun plan à afficher." }}
        />
        <div className="plan-management__pagination">
          <Text type="secondary">{formatSubscriptionNumber(pagination.total)} plan(s)</Text>
          <Pagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            showSizeChanger
            pageSizeOptions={["10", "20", "50"]}
            onChange={fetchPlans}
          />
        </div>
      </Card>

      <Modal
        centered
        open={modalOpen}
        onCancel={closePlanModal}
        footer={null}
        width={860}
        className="plan-editor-modal"
        title={
          <div className="plan-editor-modal__title">
            <span><CrownOutlined /></span>
            <div><strong>{editingPlan ? "Modifier le plan" : "Créer un plan"}</strong><small>{editingPlan?.name || "Nouvelle offre ELSA GESTION"}</small></div>
          </div>
        }
      >
        <Alert
          type="info"
          showIcon
          message="Périmètre actuel du plan"
          description="Cette fiche configure uniquement les capacités et la présentation de l'offre. Le prix est calculé séparément par Warehouse actif."
        />
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div className="plan-editor-modal__section">
            <h3>Identité du plan</h3>
            <Row gutter={14}>
              <Col xs={24} md={16}>
                <Form.Item name="name" label="Nom du plan" rules={[{ required: true, whitespace: true, message: "Nom requis" }]}>
                  <Input placeholder="Ex. Business" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="sort_order" label="Ordre d'affichage"><InputNumber min={0} precision={0} className="plan-editor-modal__full" /></Form.Item>
              </Col>
            </Row>
            <Form.Item name="description" label="Description"><TextArea rows={3} maxLength={500} showCount /></Form.Item>
          </div>

          <Divider />
          <div className="plan-editor-modal__section">
            <h3>Capacités incluses</h3>
            <Row gutter={14}>
              <Col xs={24} md={8}>
                <Form.Item name="max_users" label="Utilisateurs maximum" tooltip="Laisser vide pour illimité"><InputNumber min={1} precision={0} placeholder="Illimité" className="plan-editor-modal__full" /></Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="max_companies" label="Entreprises maximum" rules={[{ required: true, message: "Valeur requise" }]}><InputNumber min={1} precision={0} className="plan-editor-modal__full" /></Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="storage_limit_gb" label="Stockage maximum (Go)" tooltip="Laisser vide pour illimité"><InputNumber min={1} precision={0} placeholder="Illimité" className="plan-editor-modal__full" /></Form.Item>
              </Col>
            </Row>
            <Form.Item name="features" label="Fonctionnalités décrites" tooltip="Une fonctionnalité par ligne. Cette liste reste informative pour le moment.">
              <TextArea rows={5} placeholder={"Gestion commerciale\nStock et logistique\nSupport prioritaire"} />
            </Form.Item>
          </div>

          <Divider />
          <div className="plan-editor-modal__section">
            <h3>Disponibilité</h3>
            <Row gutter={14} align="middle">
              <Col xs={24} md={6}><Form.Item name="is_trial" label="Plan d'essai" valuePropName="checked"><Switch /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="trial_duration_days" label="Durée de l'essai (jours)"><InputNumber min={1} max={365} precision={0} className="plan-editor-modal__full" /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="is_active" label="Disponible" valuePropName="checked"><Switch /></Form.Item></Col>
              <Col xs={24} md={6}><Form.Item name="is_public" label="Visible sur la landing page" valuePropName="checked"><Switch /></Form.Item></Col>
            </Row>
          </div>

          <div className="plan-editor-modal__footer">
            <Button onClick={closePlanModal}>Annuler</Button>
            <Button type="primary" htmlType="submit" loading={saving}>{editingPlan ? "Enregistrer les modifications" : "Créer le plan"}</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
